import type { CreatorActivity } from "@unbox/api";
import { useCallback, useMemo, useRef, useState } from "react";
import { useCandidateActivities, useCreatorActivities } from "@/api/message-creator";
import { useCreatorUpcomingAutomations } from "@/api/sequences";
import { ActivityDaySection } from "@/components/activities/activity-day-section";
import { UpcomingAutomations } from "@/components/activities/upcoming-automations";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Tabs } from "@/components/application/tabs/tabs";
import { useAuth } from "@/providers/auth-provider";
import { useMerchant } from "@/providers/MerchantProvider";
import { cx } from "@/utils/cx";
import { groupActivitiesByDay } from "@/utils/date-grouping";

// Sub-tabs for interactions
const activityTabs = [
  { id: "all" as const, label: "All Activities" },
  { id: "messages" as const, label: "Messages" },
  { id: "activities" as const, label: "Activities" },
];

type activityTabProps = {
  data: {
    creatorId: string;
    creatorName: string;
    creatorDisplayName?: string | null;
    creatorAvatar?: string | null;
  };
  /** Type of profile: 'creator' uses username, 'candidate' uses candidateId */
  profileType?: "creator" | "candidate";
};

export const ActivityTab = ({ data, profileType = "creator" }: activityTabProps) => {
  const [selectedSubTab, setSelectedSubTab] = useState<"all" | "messages" | "activities">("all");

  // Conditionally render the appropriate activity component based on profile type
  if (profileType === "candidate") {
    return <CandidateActivityTab data={data} selectedSubTab={selectedSubTab} setSelectedSubTab={setSelectedSubTab} />;
  }

  return <CreatorActivityTab data={data} selectedSubTab={selectedSubTab} setSelectedSubTab={setSelectedSubTab} />;
};

// Creator-specific activity tab
const CreatorActivityTab = ({
  data,
  selectedSubTab,
  setSelectedSubTab,
}: {
  data: { creatorId: string; creatorName: string; creatorDisplayName?: string | null; creatorAvatar?: string | null };
  selectedSubTab: "all" | "messages" | "activities";
  setSelectedSubTab: (tab: "all" | "messages" | "activities") => void;
}) => {
  // Map sub-tab to API filter
  const apiFilter = selectedSubTab === "all" ? "all" : selectedSubTab === "messages" ? "messages" : "activities";

  // Fetch creator activities
  const {
    data: activitiesData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCreatorActivities(data.creatorName, apiFilter);
  const { data: upcomingAutomations = [] } = useCreatorUpcomingAutomations(data.creatorId);

  // Process activities from paginated response
  const activities: CreatorActivity[] = useMemo(() => {
    if (!activitiesData?.pages) return [];
    // Simply flatten all pages - API returns activities in descending order (newest first)
    return activitiesData.pages.flatMap((page) => page.activities);
  }, [activitiesData?.pages]);

  // Group activities by day
  const groupedActivities = useMemo(() => {
    const grouped = groupActivitiesByDay(activities);
    // Convert to array and sort by date (newest first)
    return Array.from(grouped.values()).sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [activities]);

  // Scroll container ref for infinite scroll
  const activitiesContainerRef = useRef<HTMLDivElement>(null);

  // Infinite scroll handler - loads more when scrolling near bottom
  const handleScroll = useCallback(() => {
    const container = activitiesContainerRef.current;
    if (!container || isFetchingNextPage || !hasNextPage) return;

    // Calculate distance from bottom
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;

    // Trigger fetch when within 200px of bottom
    if (distanceFromBottom < 200) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Get current user and merchant for avatars
  const { user } = useAuth();
  const currentMerchantId = useMerchant();

  // Get merchant logo for brand activities
  const brandAvatar = useMemo(() => {
    if (!user?.merchants || !currentMerchantId) return null;
    const currentMerchant = user.merchants.find((m) => m.id === currentMerchantId);
    return currentMerchant?.logoUrl || null;
  }, [user?.merchants, currentMerchantId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 h-full bg-gray-50 pt-5">
      {/* Sub-tabs */}
      <div className="flex justify-end flex-shrink-0 pr-5">
        <Tabs selectedKey={selectedSubTab} onSelectionChange={(key) => setSelectedSubTab(key as typeof selectedSubTab)}>
          <Tabs.List
            type="button-border"
            items={activityTabs}
            size="sm"
            className="bg-white ring-1"
            style={{ borderColor: "#D5D7DA" }}
          >
            {(tab) => (
              <Tabs.Item
                {...tab}
                className={(state) =>
                  cx(
                    "text-sm font-semibold py-1 px-4 transition-colors rounded-lg",
                    state.isSelected
                      ? "bg-gray-50 border-2 border-black shadow-none"
                      : "bg-white border-2 border-transparent hover:bg-gray-200",
                  )
                }
                style={{ color: "#414651" }}
              />
            )}
          </Tabs.List>
        </Tabs>
      </div>

      <div className="flex justify-center px-4">
        <UpcomingAutomations automations={upcomingAutomations} />
      </div>

      {/* Activities List - Scrollable container with infinite scroll */}
      <div
        ref={activitiesContainerRef}
        onScroll={handleScroll}
        className="flex-1 flex flex-col gap-0 overflow-y-auto min-h-0 items-center rounded-lg p-4"
      >
        {groupedActivities.length > 0 ? (
          <>
            {/* Activities grouped by day */}
            {groupedActivities.map((dayGroup) => (
              <ActivityDaySection
                key={dayGroup.dateLabel}
                date={dayGroup.dateLabel}
                activities={dayGroup.activities}
                creatorAvatar={data.creatorAvatar || null}
                brandAvatar={brandAvatar}
                creatorName={data.creatorDisplayName ?? data.creatorName}
              />
            ))}

            {/* Loading indicator when fetching more activities */}
            {isFetchingNextPage && (
              <div className="flex items-center justify-center py-4">
                <LoadingIndicator />
              </div>
            )}

            {/* End of list message */}
            {!isFetchingNextPage && !hasNextPage && (
              <div className="text-center py-4">
                <p className="text-xs text-tertiary">No more activities to load</p>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-sm text-tertiary">
              {selectedSubTab === "messages" && "No messages found"}
              {selectedSubTab === "activities" && "No activities found"}
              {selectedSubTab === "all" && "No activities found"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Candidate-specific activity tab
const CandidateActivityTab = ({
  data,
  selectedSubTab,
  setSelectedSubTab,
}: {
  data: { creatorId: string; creatorAvatar?: string | null };
  selectedSubTab: "all" | "messages" | "activities";
  setSelectedSubTab: (tab: "all" | "messages" | "activities") => void;
}) => {
  // Map sub-tab to API filter
  const apiFilter = selectedSubTab === "all" ? "all" : selectedSubTab === "messages" ? "messages" : "activities";

  // Fetch candidate activities with server-side filtering
  const {
    data: activitiesData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCandidateActivities(data.creatorId, apiFilter);

  // Process activities from paginated response
  const activities: CreatorActivity[] = useMemo(() => {
    if (!activitiesData?.pages) return [];
    return activitiesData.pages.flatMap((page) => page.activities);
  }, [activitiesData?.pages]);

  // Group activities by day
  const groupedActivities = useMemo(() => {
    const grouped = groupActivitiesByDay(activities);
    // Convert to array and sort by date (newest first)
    return Array.from(grouped.values()).sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [activities]);

  // Scroll container ref for infinite scroll
  const activitiesContainerRef = useRef<HTMLDivElement>(null);

  // Infinite scroll handler - loads more when scrolling near bottom
  const handleScroll = useCallback(() => {
    const container = activitiesContainerRef.current;
    if (!container || isFetchingNextPage || !hasNextPage) return;

    // Calculate distance from bottom
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;

    // Trigger fetch when within 200px of bottom
    if (distanceFromBottom < 200) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Get current user and merchant for avatars
  const { user } = useAuth();
  const currentMerchantId = useMerchant();

  // Get merchant logo for brand activities
  const brandAvatar = useMemo(() => {
    if (!user?.merchants || !currentMerchantId) return null;
    const currentMerchant = user.merchants.find((m) => m.id === currentMerchantId);
    return currentMerchant?.logoUrl || null;
  }, [user?.merchants, currentMerchantId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 h-full bg-gray-50 pt-5">
      {/* Sub-tabs */}
      <div className="flex justify-end flex-shrink-0 pr-5">
        <Tabs selectedKey={selectedSubTab} onSelectionChange={(key) => setSelectedSubTab(key as typeof selectedSubTab)}>
          <Tabs.List
            type="button-border"
            items={activityTabs}
            size="sm"
            className="bg-white ring-1"
            style={{ borderColor: "#D5D7DA" }}
          >
            {(tab) => (
              <Tabs.Item
                {...tab}
                className={(state) =>
                  cx(
                    "text-sm font-semibold py-1 px-4 transition-colors rounded-lg",
                    state.isSelected
                      ? "bg-gray-50 border-2 border-black shadow-none"
                      : "bg-white border-2 border-transparent hover:bg-gray-200",
                  )
                }
                style={{ color: "#414651" }}
              />
            )}
          </Tabs.List>
        </Tabs>
      </div>

      {/* Activities List - Scrollable container with infinite scroll */}
      <div
        ref={activitiesContainerRef}
        onScroll={handleScroll}
        className="flex-1 flex flex-col gap-0 overflow-y-auto min-h-0 items-center items-center"
      >
        {groupedActivities.length > 0 ? (
          <>
            {/* Activities grouped by day */}
            {groupedActivities.map((dayGroup) => (
              <ActivityDaySection
                key={dayGroup.dateLabel}
                date={dayGroup.dateLabel}
                activities={dayGroup.activities}
                creatorAvatar={data.creatorAvatar || null}
                brandAvatar={brandAvatar}
                creatorName={data.creatorId}
              />
            ))}

            {/* Loading indicator when fetching more activities */}
            {isFetchingNextPage && (
              <div className="flex items-center justify-center py-4">
                <LoadingIndicator />
              </div>
            )}

            {/* End of list message */}
            {!isFetchingNextPage && !hasNextPage && (
              <div className="text-center py-4">
                <p className="text-xs text-tertiary">No more activities to load</p>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-sm text-tertiary">
              {selectedSubTab === "messages" && "No messages found"}
              {selectedSubTab === "activities" && "No activities found"}
              {selectedSubTab === "all" && "No activities found"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
